import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { MoreVerticalIcon, PencilIcon, UserRoundPlusIcon, WorkflowIcon } from 'lucide-react';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderEditableName,
  PageHeaderIdentity,
} from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import { useHasProjectPermission } from '@/entities/user';
import { ScopedPermission } from '@/shared/ui';
import type { ProjectDetailHeaderProps } from './ProjectDetailHeader.types';
import { useTranslation } from 'react-i18next';

export function ProjectDetailHeader({
  project,
  onShare,
  shareDisabled,
  actions,
  onManageApprovalChains,
  onEditProjectDetail,
  onRename,
}: ProjectDetailHeaderProps) {
  const { t } = useTranslation('projects');

  // Prefer the loaded members list when it has 2+ entries (multi-member
  // project). For 0/1 members, or while members are still loading, fall
  // back to the single creator avatar to preserve the existing visual
  // contract — a solo project should not render an empty/odd-looking
  // AvatarGroup.

  // Hidden rather than disabled, so an unpermitted caller gets no menu at all.
  // `ScopedPermission` cannot do this: it checks `fallback` for truthiness, so
  // `null` reads as "not supplied" and it disables the trigger instead.
  //
  // The two entries answer to different permissions, so each is resolved on its
  // own and the trigger appears only when at least one survives.
  const canEditDetails = useHasProjectPermission([PROJECT_PERMISSIONS.ProjectEditDetails]);
  // Changing chains needs member management too: a step may name a role, and only those who
  // manage members can see who holds it.
  const holdsChainPermission = useHasProjectPermission([PROJECT_PERMISSIONS.ManageApprovalChains]);
  const canManageMembers = useHasProjectPermission([PROJECT_PERMISSIONS.ManageProjectMembers]);
  const canManageApprovalChains = holdsChainPermission && canManageMembers;
  const showManageApprovalChains = !!onManageApprovalChains && canManageApprovalChains;
  const showEditProjectDetail = !!onEditProjectDetail && canEditDetails;
  const showMenu = showManageApprovalChains || showEditProjectDetail;

  const actionGroup = (
    <>
      {actions}

      <ScopedPermission permissions={[PROJECT_PERMISSIONS.ManageProjectMembers]}>
        <Button variant="outline" size="lg" onClick={onShare} disabled={shareDisabled}>
          <UserRoundPlusIcon className="size-4" />
          {t('projectDetailHeader.share')}
        </Button>
      </ScopedPermission>

      {showMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-lg"
              aria-label={t('projectDetailHeader.projectActions')}
              data-testid="project-actions-menu-trigger"
            >
              <MoreVerticalIcon className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {showManageApprovalChains && (
              <DropdownMenuItem
                onSelect={onManageApprovalChains}
                data-testid="manage-approval-chains-menu-item"
              >
                <WorkflowIcon className="size-4" />
                {t('projectDetailHeader.manageApprovalChains')}
              </DropdownMenuItem>
            )}

            {showManageApprovalChains && showEditProjectDetail && <DropdownMenuSeparator />}

            {showEditProjectDetail && (
              <DropdownMenuItem
                onSelect={onEditProjectDetail}
                data-testid="edit-project-detail-menu-item"
              >
                <PencilIcon className="size-4" />
                {t('projectDetailHeader.editProjectDetail')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );

  return (
    <PageHeader>
      <PageHeaderIdentity>
        <PageHeaderBreadcrumb>
          <PageHeaderBreadcrumbLink href={ROUTES.PROJECTS}>
            {t('projects.projects')}
          </PageHeaderBreadcrumbLink>
          {onRename ? (
            <PageHeaderEditableName
              value={project.title}
              label={t('projectDetailHeader.projectName')}
              onCommit={onRename}
            />
          ) : (
            <PageHeaderBreadcrumbPage>{project.title}</PageHeaderBreadcrumbPage>
          )}
        </PageHeaderBreadcrumb>
      </PageHeaderIdentity>
      <PageHeaderActions>{actionGroup}</PageHeaderActions>
    </PageHeader>
  );
}
